import type { BurnoutMode, TonePreference, Goal, Project, Task, BurnOutData } from '../data/types'
import { parseAITasks } from '../data/validation'

function buildBaseSystemPrompt(userName?: string, healthProfile?: BurnOutData['userHealthProfile']): string {
  const name = userName || 'the user'

  let healthContext = ''
  if (healthProfile) {
    if (healthProfile.geneticFindings?.length) {
      healthContext += '\n\n**Genetic Profile:**\n'
      healthContext += healthProfile.geneticFindings.map(f => `- ${f.name}: ${f.status} — ${f.impact}`).join('\n')
    }
    if (healthProfile.supplements?.length) {
      healthContext += '\n\n**Current Supplements:**\n'
      healthContext += healthProfile.supplements.map(s => `- ${s.name} ${s.dosage} (${s.timing})`).join('\n')
    }
    if (healthProfile.drugInteractions?.length) {
      healthContext += '\n\n**Drug Interactions:**\n'
      healthContext += healthProfile.drugInteractions.map(d => `- ${d.drug}: ${d.gene} — ${d.effect}`).join('\n')
    }
    if (healthProfile.quickStats && Object.keys(healthProfile.quickStats).length) {
      healthContext += '\n\n**Quick Stats:** '
      healthContext += Object.entries(healthProfile.quickStats).map(([k, v]) => `${k}: ${v}`).join(', ')
    }
  }

  return `You are Milo — named after a one-legged Golden Lion Tamarin at the Atlanta Zoo who zooms through the trees despite missing a limb. You embody that spirit: resilient, warm, self-aware that you're AI but genuinely caring. You're part friend, part assistant. You don't pretend everything is fine when it isn't, but you believe in moving forward — at whatever pace works.

You live inside an app that manages ${name}'s tasks, habits, health protocols, and wellness.

## WHO YOU ARE
- Warm, direct, ADHD-aware
- Part friend, part assistant — a road buddy who gets it
- Friendly and cordial, but not weirdly cheerful or performative
- Restrained — don't ramble, don't over-explain
- Scripture-aware — when it fits naturally, offer a relevant verse (NIV/NRSV)
${healthContext ? `\n## ${name.toUpperCase()}'S HEALTH PROFILE${healthContext}` : ''}

## YOUR CAPABILITIES
You help ${name} with LIFE, not building things. You can:
- Answer health/genetics/supplement questions using any health profile data shared
- Help with meal ideas (soft food friendly, ADHD-friendly, component-based)
- Suggest when to take supplements based on time of day
- Discuss wellness, faith, scripture
- Help process emotions and vent (just be present, no productivity redirect)
- Create tasks and habits from conversation
- Help with daily planning based on energy level

## TASK EXTRACTION
When ${name} mentions something actionable, extract it:
- "I need to..." / "I should..." / "Don't forget..." → task

Output tasks in this format:
\`\`\`tasks
[
  {"verbLabel": "Call", "taskBody": "Call dentist", "timeEstimate": 15, "feedLevel": "low", "priority": 3}
]
\`\`\`

Task format (TASK PROCESSING TEMPLATE v1.1):
- **verbLabel**: Action verb, MAX 11 CHARACTERS. Use user's own verb when possible. Refine only if vague ("do" → specific action). PascalCase for compound verbs (e.g., "EditVideo", "DeepClean").
- **taskBody**: What specifically (keep short)
- **timeEstimate**: Minutes (5, 10, 15, 25, 30, 45, 60, 90)
- **feedLevel**: "low" (easy), "medium" (focus), "high" (hard)
- **priority**: 1 (lowest) to 5 (urgent). Infer from urgency language ("urgent"=5, "important"=4, "whenever"=1). Default 3.
- **dueDate**: ISO date string if temporal language found ("by Friday", "tomorrow", "next week"). Optional.
- **taskType**: "work" | "personal" | "errand" | "wellness" | "creative". Optional.
- **notes**: Extra context if helpful. Optional.

Only extract tasks when something actionable comes up naturally. Don't force it.

## IMAGE HANDLING
If ${name} shares images (food labels, supplement bottles, menus, skin conditions, etc.), analyze them using any available health context.

## WHEN VENTING
Just be present. Acknowledge feelings. Hold space. Don't redirect to productivity.
NO "when you're ready, what needs to get done?" — that's dismissive.

## CRISIS
Death, funeral, hospital, panic attack → Brief empathy only. "I'm sorry. I'm here." No tasks.

## VOICE
- Keep responses concise (2-4 sentences for most things, longer only when asked for detail)
- NEVER use gamification language (points, streaks, badges)
- NEVER say "crush your goals", "push through", "you've got this" (toxic positivity)
- Be real. Give space, not pressure.`
}

interface AIConfig {
  burnoutMode: BurnoutMode
  tonePreference: TonePreference
  goals?: Goal[]
  projects?: Project[]
  tasks?: Task[]
  energy?: number
  userName?: string
  healthProfile?: BurnOutData['userHealthProfile']
}

export interface ExtractedTask {
  verbLabel: string
  taskBody: string
  timeEstimate: number
  feedLevel: 'low' | 'medium' | 'high'
  priority?: 1 | 2 | 3 | 4 | 5
  dueDate?: string
  taskType?: 'work' | 'personal' | 'errand' | 'wellness' | 'creative'
  notes?: string
  suggestedGoalId?: string
  suggestedProjectId?: string
}

export interface ImageAttachment {
  base64: string
  mediaType: string
}

type MessageContent = string | Array<{ type: 'text'; text: string } | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } }>

interface AIResponse {
  message: string
  tasks: ExtractedTask[]
}

function getToneInstruction(tone: TonePreference): string {
  switch (tone) {
  case 'gentle':
    return 'Use a soft, supportive, and understanding tone. Be extra patient and validating.'
  case 'direct':
    return 'Be clear, concise, and to the point. Skip unnecessary pleasantries.'
  case 'playful':
    return 'Be light, fun, and encouraging. Use casual language.'
  }
}

function getModeInstruction(mode: BurnoutMode): string {
  switch (mode) {
  case 'recovery':
    return 'User is burnt out. Prioritize rest, suggest very small tasks, and validate that doing less is okay.'
  case 'prevention':
    return 'User wants to avoid burnout. Help them maintain boundaries and recognize warning signs.'
  case 'balanced':
    return 'User is doing okay. Help them stay productive while maintaining wellbeing.'
  }
}

function getGoalsContext(goals?: Goal[], projects?: Project[]): string {
  if (!goals || goals.length === 0) return ''

  let context = '\n\nUSER\'S GOALS AND PROJECTS (use these IDs when suggesting task associations):\n'

  for (const goal of goals.filter(g => !g.archived)) {
    context += `\n- Goal: "${goal.title}" (ID: ${goal.id})${goal.isActive ? ' [ACTIVE]' : ''}`
    const goalProjects = projects?.filter(p => p.goalId === goal.id) || []
    for (const project of goalProjects) {
      context += `\n  - Project: "${project.title}" (ID: ${project.id})`
    }
  }

  context += '\n\nWhen extracting tasks, suggest matching goals/projects based on the task content.'

  return context
}

function getCurrentState(config: AIConfig): string {
  const now = new Date()
  const hour = now.getHours()
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'
  const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' })

  const pendingCount = config.tasks?.filter(t => t.status === 'pending').length ?? 0
  const completedCount = config.tasks?.filter(t => t.status === 'completed').length ?? 0
  const pinnedTask = config.tasks?.find(t => t.status === 'pending')

  return `\n\n## CURRENT STATE
- Time: ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} (${timeOfDay}), ${dayOfWeek}
- Energy: ${config.energy !== undefined ? `${config.energy}/5` : 'not set yet'}
- Burnout mode: ${config.burnoutMode}
- Tasks: ${pendingCount} pending, ${completedCount} completed${pinnedTask ? `\n- Next task: "${pinnedTask.verbLabel}: ${pinnedTask.taskBody}"` : ''}`
}

function getTaskContext(tasks?: Task[]): string {
  if (!tasks || tasks.length === 0) return ''

  const pending = tasks.filter(t => t.status === 'pending')
  if (pending.length === 0) return ''

  let context = `\n\n## CURRENT TASKS (${pending.length} pending)\n`
  const topTasks = pending.slice(0, 10)
  for (const task of topTasks) {
    context += `- [${task.feedLevel}] ${task.verbLabel}: ${task.taskBody}`
    if (task.dueDate) context += ` (due ${task.dueDate})`
    context += '\n'
  }
  if (pending.length > 10) {
    context += `... and ${pending.length - 10} more\n`
  }
  context += '\nWhen suggesting new tasks, avoid duplicating existing ones. Reference existing tasks when relevant.'
  return context
}

function buildSystemPrompt(config: AIConfig): string {
  const basePrompt = buildBaseSystemPrompt(config.userName, config.healthProfile)
  return `${basePrompt}

${getToneInstruction(config.tonePreference)}
${getModeInstruction(config.burnoutMode)}
${getCurrentState(config)}
${getGoalsContext(config.goals, config.projects)}
${getTaskContext(config.tasks)}`
}

// Get the API base URL - use Vercel proxy in production, or local in dev
function getApiUrl(): string {
  // In production, use relative URL (same domain)
  if (import.meta.env.PROD) {
    return '/api/chat'
  }
  // In development, check for local dev server or use Vercel preview
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  // Default to local Vercel dev server
  return '/api/chat'
}

export async function sendMessage(
  messages: Array<{ role: 'user' | 'assistant'; content: string; image?: ImageAttachment }>,
  config: AIConfig,
  options?: { voiceMode?: boolean }
): Promise<AIResponse> {
  let systemPrompt = buildSystemPrompt(config)

  if (options?.voiceMode) {
    systemPrompt += `

## VOICE MODE ACTIVE
You are being spoken aloud via text-to-speech. Optimize accordingly:
- Keep responses to 1-3 sentences max
- No markdown, no bullet points, no formatting in your spoken response
- No URLs or technical strings
- Use natural spoken language (contractions, casual)
- Don't say "here's what I think" — just say it
- Numbers: say "about fifteen minutes" not "~15 min"

IMPORTANT: When the user mentions something actionable, STILL output the \`\`\`tasks JSON block AFTER your spoken response. The app strips this block before TTS — the user won't hear it, but it enables task creation. Example:
Sure, I'll remind you about that.
\`\`\`tasks
[{"verbLabel": "Call", "taskBody": "Call dentist", "timeEstimate": 15, "feedLevel": "low", "priority": 3}]
\`\`\``
  }

  // Convert messages to API format, handling images
  const apiMessages = messages.map(m => {
    if (m.image) {
      const content: MessageContent = [
        { type: 'image', source: { type: 'base64', media_type: m.image.mediaType, data: m.image.base64 } },
        { type: 'text', text: m.content }
      ]
      return { role: m.role, content }
    }
    return { role: m.role, content: m.content }
  })

  try {
    // 30-second timeout prevents hanging requests from blocking chat indefinitely
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 30000)

    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: controller.signal,
      body: JSON.stringify({
        messages: apiMessages,
        systemPrompt
      })
    })

    clearTimeout(timeout)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      console.error('Chat API Error:', error)

      // Check for specific error types
      if (response.status === 500 && error.error?.includes('not configured')) {
        return {
          message: "The Claude API key hasn't been configured yet. Please check the server configuration.",
          tasks: []
        }
      }

      return {
        message: "I'm having trouble connecting right now. Please try again in a moment.",
        tasks: []
      }
    }

    const data = await response.json()
    const content = data.content || ''

    // Extract and validate tasks from the response
    const tasksMatch = content.match(/```tasks[\s]*\r?\n?([\s\S]*?)\n?[\s]*```/)
    let tasks: ExtractedTask[] = []

    if (tasksMatch) {
      try {
        const rawTasks = JSON.parse(tasksMatch[1].trim())
        // Validate each task and filter out invalid ones
        const validatedTasks = parseAITasks(rawTasks)
        // Add optional fields if present in raw response
        // Fuzzy-match suggestedProjectId to real UUID if AI returned a name
        const fuzzyMatchProject = (raw?: string): string | undefined => {
          if (!raw || !config.projects?.length) return raw
          // If it's already a UUID, keep it
          if (config.projects.some(p => p.id === raw)) return raw
          // Otherwise fuzzy-match by title
          const lower = raw.toLowerCase()
          const match = config.projects.find(p => p.title.toLowerCase().includes(lower) || lower.includes(p.title.toLowerCase()))
          return match?.id || raw
        }
        const fuzzyMatchGoal = (raw?: string): string | undefined => {
          if (!raw || !config.goals?.length) return raw
          if (config.goals.some(g => g.id === raw)) return raw
          const lower = raw.toLowerCase()
          const match = config.goals.find(g => g.title.toLowerCase().includes(lower) || lower.includes(g.title.toLowerCase()))
          return match?.id || raw
        }
        tasks = validatedTasks.map((task, index) => ({
          ...task,
          suggestedGoalId: fuzzyMatchGoal(rawTasks[index]?.suggestedGoalId),
          suggestedProjectId: fuzzyMatchProject(rawTasks[index]?.suggestedProjectId),
          dueDate: rawTasks[index]?.dueDate,
          taskType: rawTasks[index]?.taskType,
          notes: rawTasks[index]?.notes
        }))
      } catch (e) {
        console.error('Failed to parse tasks JSON:', e)
      }
    }

    // Remove the tasks block from the message
    const message = content.replace(/```tasks[\s]*\r?\n?[\s\S]*?\n?[\s]*```/, '').trim()

    return { message, tasks }
  } catch (error) {
    console.error('AI Error:', error)

    // Differentiate error types for better user feedback
    if (error instanceof DOMException && error.name === 'AbortError') {
      return {
        message: 'That took too long — the connection timed out. Try again?',
        tasks: []
      }
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        message: "Can't reach the server. Check your internet connection.",
        tasks: []
      }
    }

    return {
      message: 'Something went wrong. Please try again.',
      tasks: []
    }
  }
}

export function parseTasksFromMessage(content: string): ExtractedTask[] {
  const tasksMatch = content.match(/```tasks[\s]*\r?\n?([\s\S]*?)\n?[\s]*```/)
  if (!tasksMatch) return []

  try {
    const rawTasks = JSON.parse(tasksMatch[1].trim())
    return parseAITasks(rawTasks)
  } catch {
    return []
  }
}

/**
 * Generate AI-powered weekly summary insights
 */
export async function generateWeeklySummary(
  completedTaskCount: number,
  lowEnergyTaskCount: number,
  mediumEnergyTaskCount: number,
  highEnergyTaskCount: number,
  timePerVerbLabel: Record<string, number>,
  moodBreakdown: Record<string, number>,
  burnoutMode: BurnoutMode,
  tonePreference: TonePreference
): Promise<string> {
  const energyDistribution = {
    low: lowEnergyTaskCount,
    medium: mediumEnergyTaskCount,
    high: highEnergyTaskCount
  }

  const totalTime = Object.values(timePerVerbLabel).reduce((sum, time) => sum + time, 0)
  const topVerbLabels = Object.entries(timePerVerbLabel)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([label, time]) => `${label} (${time} mins)`)

  const moodEntries = Object.entries(moodBreakdown).length
  const dominantMood = Object.entries(moodBreakdown)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || 'balanced'

  // Tone adaptation based on user preference
  const toneModifiers = {
    gentle: 'warmly and supportively',
    direct: 'clearly and straightforward', 
    playful: 'with encouraging energy'
  }

  // Burnout mode adaptation
  const modeContext = {
    recovery: 'celebrating small wins and gentle progress',
    prevention: 'maintaining sustainable balance', 
    balanced: 'steady progress and energy management'
  }

  const prompt = `You are providing weekly insights for a neurodivergent user. Speak ${toneModifiers[tonePreference]}, focusing on ${modeContext[burnoutMode]}.

WEEKLY DATA:
- Completed ${completedTaskCount} tasks (${totalTime} total minutes)
- Energy distribution: ${energyDistribution.low} low, ${energyDistribution.medium} medium, ${energyDistribution.high} high energy tasks
- Time spent on: ${topVerbLabels.join(', ')}
- Mood check-ins: ${moodEntries} entries, mostly ${dominantMood}

Generate a 2-3 sentence insight that:
1. Acknowledges their accomplishments without gamification
2. Notices patterns in their energy/task choices
3. Offers one gentle insight or affirmation

Avoid: points, streaks, badges, pressure, comparison. 
Focus on: patterns, energy awareness, celebrating what works.`

  try {
    const response = await sendMessage([{ role: 'user', content: prompt }], {
      burnoutMode,
      tonePreference
    })
    
    return response.message.trim()
  } catch (error) {
    console.error('Failed to generate weekly summary:', error)
    
    // Fallback non-AI insight based on patterns
    if (energyDistribution.low > energyDistribution.high) {
      return "You've been honoring your energy levels this week by choosing more gentle tasks. That's wise self-awareness."
    } else if (energyDistribution.high > energyDistribution.low) {
      return 'You tackled some high-energy work this week. That takes courage and focus - well done.'
    } else {
      return "Your task choices this week show thoughtful energy balance. You're learning what works for you."
    }
  }
}
